/**
 * Embedding Service
 * Handles text embedding generation via Featherless Qwen3-Embedding-8B (server-side Vercel API)
 *
 * Model: Qwen/Qwen3-Embedding-8B (4000 dimensions via MRL, stored as halfvec)
 */

import { getCurrentSession } from '@/lib/supabase';

export const EMBEDDING_MODEL = 'Qwen/Qwen3-Embedding-8B';
export const EMBEDDING_DIMENSIONS = 4000;

export interface EmbeddingResult {
  embedding: number[];
  model: string;
  dimensions: number;
  usage?: {
    prompt_tokens?: number;
    total_tokens?: number;
  };
}

export interface ChunkResult {
  text: string;
  index: number;
  tokens: number;
}

const DEFAULT_DOC_CHUNK_SIZE = 2500;
const DEFAULT_DOC_OVERLAP = 200;

class EmbeddingService {
  private defaultModel = EMBEDDING_MODEL;
  private dimensions = EMBEDDING_DIMENSIONS;

  private async authHeaders(): Promise<HeadersInit> {
    const session = await getCurrentSession();
    if (!session?.access_token) throw new Error('Unauthorized');
    return {
      Authorization: `Bearer ${session.access_token}`,
      'Content-Type': 'application/json',
    };
  }

  private async callEmbedApi(body: Record<string, unknown>): Promise<unknown> {
    const res = await fetch('/api/embeddings?route=embed', {
      method: 'POST',
      headers: await this.authHeaders(),
      body: JSON.stringify(body),
    });
    if (!res.ok) {
      const errText = await res.text();
      let message = errText;
      try {
        message = JSON.parse(errText).error || errText;
      } catch {
        // keep errText
      }
      throw new Error(message || `Embedding API error ${res.status}`);
    }
    return res.json();
  }

  async generateEmbedding(
    text: string,
    options: { model?: string } = {}
  ): Promise<EmbeddingResult> {
    if (!text?.trim()) throw new Error('Cannot generate embedding for empty text');

    const data = (await this.callEmbedApi({
      input: text,
      model: options.model || this.defaultModel,
    })) as {
      embedding?: number[];
      model?: string;
      dimensions?: number;
      usage?: EmbeddingResult['usage'];
    };

    if (!Array.isArray(data.embedding)) {
      throw new Error('Invalid embedding response format (missing embedding array)');
    }

    const actualDimensions = data.dimensions || data.embedding.length;
    this.dimensions = actualDimensions;

    return {
      embedding: data.embedding,
      model: data.model || this.defaultModel,
      dimensions: actualDimensions,
      usage: data.usage,
    };
  }

  async generateEmbeddings(
    texts: string[],
    options: { model?: string; batchSize?: number; delayMs?: number } = {}
  ): Promise<EmbeddingResult[]> {
    const { model, batchSize = 10, delayMs = 150 } = options;
    const results: EmbeddingResult[] = [];

    for (let i = 0; i < texts.length; i += batchSize) {
      const batch = texts.slice(i, i + batchSize);
      const data = (await this.callEmbedApi({
        input: batch,
        model: model || this.defaultModel,
      })) as {
        embeddings?: number[][];
        model?: string;
        usage?: EmbeddingResult['usage'];
      };

      if (!Array.isArray(data.embeddings)) {
        throw new Error('Invalid batch embedding response: missing embeddings array');
      }

      for (const embedding of data.embeddings) {
        results.push({
          embedding,
          model: data.model || this.defaultModel,
          dimensions: embedding.length,
          usage: data.usage,
        });
      }

      if (i + batchSize < texts.length) {
        await new Promise((r) => setTimeout(r, delayMs));
      }
    }

    return results;
  }

  async generateEmbeddingsBatch(
    texts: string[],
    options: { model?: string; maxBatchSize?: number; delayMs?: number } = {}
  ): Promise<EmbeddingResult[]> {
    return this.generateEmbeddings(texts, {
      model: options.model,
      batchSize: options.maxBatchSize ?? 10,
      delayMs: options.delayMs ?? 150,
    });
  }

  chunkText(
    text: string,
    options: {
      maxChunkSize?: number;
      overlapSize?: number;
      preserveParagraphs?: boolean;
      strategy?: 'paragraph' | 'semantic';
    } = {}
  ): ChunkResult[] {
    const {
      maxChunkSize = DEFAULT_DOC_CHUNK_SIZE,
      overlapSize = DEFAULT_DOC_OVERLAP,
      preserveParagraphs = true,
      strategy = 'paragraph',
    } = options;

    if (!text?.trim()) return [];

    if (strategy === 'semantic') {
      return this.chunkTextSemantic(text, { maxChunkSize, overlapSize });
    }

    return this.chunkTextParagraph(text, { maxChunkSize, overlapSize, preserveParagraphs });
  }

  private chunkTextSemantic(
    text: string,
    options: { maxChunkSize: number; overlapSize: number }
  ): ChunkResult[] {
    const { maxChunkSize, overlapSize } = options;
    const sections = this.splitBySections(text.trim());
    const chunks: ChunkResult[] = [];
    let chunkIndex = 0;

    for (const section of sections) {
      const trimmed = section.trim();
      if (!trimmed) continue;
      if (trimmed.length <= maxChunkSize) {
        chunks.push({
          text: trimmed,
          index: chunkIndex++,
          tokens: this.estimateTokens(trimmed),
        });
        continue;
      }
      const sectionChunks = this.chunkTextParagraph(trimmed, {
        maxChunkSize,
        overlapSize,
        preserveParagraphs: true,
      });
      for (const c of sectionChunks) {
        chunks.push({ ...c, index: chunkIndex++ });
      }
    }

    return chunks;
  }

  private splitBySections(text: string): string[] {
    const sectionStart = /\n(?=(?:#{1,6}\s|---\s*\n|\d+\.\s+[A-Z]))/;
    const parts = text.split(sectionStart);
    const result: string[] = [];
    let pending = parts[0]?.trim() ?? '';
    for (let i = 1; i < parts.length; i++) {
      const part = parts[i];
      if (!part?.trim()) continue;
      if (pending) {
        result.push(pending);
        pending = '';
      }
      pending = part.trim();
    }
    if (pending) result.push(pending);
    return result.length > 0 ? result : [text];
  }

  private chunkTextParagraph(
    text: string,
    options: {
      maxChunkSize: number;
      overlapSize: number;
      preserveParagraphs: boolean;
    }
  ): ChunkResult[] {
    const { maxChunkSize, overlapSize, preserveParagraphs } = options;
    const chunks: ChunkResult[] = [];
    const paragraphs = preserveParagraphs
      ? text.split(/\n\n+/).filter((p) => p.trim().length > 0)
      : [text];

    let currentChunk = '';
    let chunkIndex = 0;

    for (const paragraph of paragraphs) {
      const trimmedParagraph = paragraph.trim();

      if (currentChunk.length + trimmedParagraph.length <= maxChunkSize) {
        currentChunk += (currentChunk ? '\n\n' : '') + trimmedParagraph;
      } else {
        if (currentChunk) {
          chunks.push({
            text: currentChunk,
            index: chunkIndex++,
            tokens: this.estimateTokens(currentChunk),
          });
        }

        if (trimmedParagraph.length > maxChunkSize) {
          const sentences = trimmedParagraph.match(/[^.!?]+[.!?]+/g) || [trimmedParagraph];
          currentChunk = '';

          for (const sentence of sentences) {
            const trimmedSentence = sentence.trim();
            if (currentChunk.length + trimmedSentence.length <= maxChunkSize) {
              currentChunk += (currentChunk ? ' ' : '') + trimmedSentence;
            } else {
              if (currentChunk) {
                chunks.push({
                  text: currentChunk,
                  index: chunkIndex++,
                  tokens: this.estimateTokens(currentChunk),
                });
                const words = currentChunk.split(' ');
                const overlapWords = words.slice(-Math.ceil(overlapSize / 5));
                currentChunk = overlapWords.join(' ') + ' ' + trimmedSentence;
              } else {
                currentChunk = trimmedParagraph.substring(0, maxChunkSize);
                chunks.push({
                  text: currentChunk,
                  index: chunkIndex++,
                  tokens: this.estimateTokens(currentChunk),
                });
                currentChunk = trimmedParagraph.substring(maxChunkSize);
              }
            }
          }
        } else {
          if (chunks.length > 0) {
            const lastChunk = chunks[chunks.length - 1].text;
            const words = lastChunk.split(' ');
            const overlapWords = words.slice(-Math.ceil(overlapSize / 5));
            currentChunk = overlapWords.join(' ') + ' ' + trimmedParagraph;
          } else {
            currentChunk = trimmedParagraph;
          }
        }
      }
    }

    if (currentChunk.trim()) {
      chunks.push({
        text: currentChunk.trim(),
        index: chunkIndex,
        tokens: this.estimateTokens(currentChunk),
      });
    }

    return chunks;
  }

  private estimateTokens(text: string): number {
    return Math.ceil(text.length / 4);
  }

  validateEmbedding(embedding: number[], expectedDimensions?: number): boolean {
    const expected = expectedDimensions || this.dimensions;
    return (
      Array.isArray(embedding) &&
      embedding.length === expected &&
      embedding.every((val) => typeof val === 'number' && !isNaN(val))
    );
  }

  cosineSimilarity(embedding1: number[], embedding2: number[]): number {
    if (embedding1.length !== embedding2.length) {
      throw new Error('Embeddings must have same dimensions');
    }

    let dotProduct = 0;
    let norm1 = 0;
    let norm2 = 0;

    for (let i = 0; i < embedding1.length; i++) {
      dotProduct += embedding1[i] * embedding2[i];
      norm1 += embedding1[i] * embedding1[i];
      norm2 += embedding2[i] * embedding2[i];
    }

    return dotProduct / (Math.sqrt(norm1) * Math.sqrt(norm2));
  }

  getModelInfo() {
    return {
      model: this.defaultModel,
      dimensions: this.dimensions,
      provider: 'Featherless',
      apiEndpoint: '/api/embeddings?route=embed',
    };
  }

  getAvailableModels(): string[] {
    return [EMBEDDING_MODEL];
  }
}

export const embeddingService = new EmbeddingService();
